/**
 * Collection endless: grid banners, metafield copy blocks, + infinite scroll helpers
 *
 * Loaded only on pages rendering `sections/collection-endless.liquid`.
 * Works in the Shopify theme editor by being:
 * - idempotent (safe to run multiple times)
 * - compatible with section re-rendering (new DOM nodes get upgraded automatically)
 * - resilient to dynamic grid updates (MutationObserver)
 */
(function () {
	if (!window.customElements) return;

	const GRID_SELECTOR = "#product-grid";
	const MOBILE_WIDTH_PX = 600; // Keep existing banner breakpoint behavior.

	let isRepositioning = false;
	let bannersScheduled = false;

	function getProductGrid(root = document) {
		return root.querySelector(GRID_SELECTOR);
	}

	function isMobileBannerLayout() {
		return window.innerWidth < MOBILE_WIDTH_PX;
	}

	function getMobileCols(productGrid) {
		if (productGrid) {
			const classes = productGrid.className;
			const match = classes.match(/wt-grid--cols-(\d+)/);
			if (match) return parseInt(match[1], 10) || 2;
		}
		return 2;
	}

	function getMdCols(productGrid) {
		if (productGrid) {
			const match = productGrid.className.match(/wt-grid--md-cols-(\d+)/);
			if (match) return parseInt(match[1], 10) || 3;
		}
		return 3;
	}

	function getDesktopCols(productGrid) {
		if (productGrid) {
			const match = productGrid.className.match(/wt-grid--lg-cols-(\d+)/);
			if (match) return parseInt(match[1], 10) || 3;
		}
		return 3;
	}

	function getCurrentCols(productGrid) {
		if (window.matchMedia("(min-width: 64em)").matches) return getDesktopCols(productGrid);
		if (window.matchMedia("(min-width: 48em)").matches) return getMdCols(productGrid);
		return getMobileCols(productGrid);
	}

	function isCopyBlockItem(el) {
		return Boolean(el && el.nodeType === 1 && el.hasAttribute("data-copy-block-index"));
	}

	function isInfoCardItem(el) {
		return Boolean(el && el.querySelector && el.querySelector(".productitem--info-block"));
	}

	function nextListItem(node) {
		let current = node;
		while (current) {
			if (current.nodeType === 1 && current.tagName === "LI") return current;
			current = current.nextSibling;
		}
		return null;
	}

	function infoCardCellSpan(cols) {
		// Match collection-grid-banners.css: info cards span the full row on small screens.
		if (window.matchMedia("(max-width: 37.99em)").matches) return cols;
		return 1;
	}

	/**
	 * Metafield copy blocks are full-width row breakers, inserted in Liquid after every
	 * N *products*. Inline info cards also occupy a grid cell, so that product count no
	 * longer lands on a row boundary — the leftover product sits alone in front of the
	 * text block. Re-place each copy block after `blockIndex * 2` visual rows, counting
	 * products and inline info cards as cells, so leftover products start after the text block.
	 */
	function placeCopyBlocks(productGrid) {
		const copyBlocks = Array.from(productGrid.querySelectorAll("li[data-copy-block-index]")).sort(
			(a, b) => (parseInt(a.dataset.copyBlockIndex, 10) || 0) - (parseInt(b.dataset.copyBlockIndex, 10) || 0)
		);
		if (!copyBlocks.length) return;

		const cols = getCurrentCols(productGrid);
		if (!cols) return;

		const rowsPerBlock = 2;
		const infoSpan = infoCardCellSpan(cols);

		for (const copyEl of copyBlocks) {
			const blockIndex = parseInt(copyEl.dataset.copyBlockIndex, 10);

			if (blockIndex === 0) {
				copyEl.remove();
				continue;
			}

			const targetRows = blockIndex * rowsPerBlock;
			let currentRow = 0;
			let cellsInRow = 0;
			let insertionPoint = null;

			for (const item of Array.from(productGrid.children)) {
				if (item.tagName !== "LI" || item === copyEl || isCopyBlockItem(item)) continue;

				const cellSpan = isInfoCardItem(item) ? infoSpan : 1;

				if (cellsInRow > 0 && cellsInRow + cellSpan > cols) {
					currentRow += 1;
					cellsInRow = 0;
					if (currentRow >= targetRows) {
						insertionPoint = item;
						break;
					}
				}

				cellsInRow += cellSpan;
				if (cellsInRow >= cols) {
					currentRow += 1;
					cellsInRow = 0;
					if (currentRow >= targetRows) {
						insertionPoint = nextListItem(item.nextSibling);
						break;
					}
				}
			}

			if (!insertionPoint || insertionPoint === copyEl) continue;
			if (copyEl.nextElementSibling === insertionPoint) continue;
			productGrid.insertBefore(copyEl, insertionPoint);
		}
	}

	function getProductCards(productGrid) {
		const allItems = productGrid.querySelectorAll('li[data-item-number]');
		return Array.from(allItems).filter((item) => {
			// Exclude info blocks (they contain productitem--info-block).
			return !item.querySelector(".productitem--info-block");
		});
	}

	function findProductCardByHandle(products, handle) {
		if (!handle) return null;
		return products.find((li) => (li.dataset.productHandle || "") === handle) || null;
	}

	function findProductCardById(products, id) {
		if (!id) return null;
		const idStr = String(id);
		return products.find((li) => (li.dataset.productId || "") === idStr) || null;
	}

	function findAnchorProduct(products, handle, id) {
		return findProductCardByHandle(products, handle) || findProductCardById(products, id);
	}

	function moreProductsWillLoad() {
		const el = document.querySelector("infinite-scroll");
		if (!el) return false;
		if (el.dataset.lastPage === "true") return false;
		return Boolean(el.dataset.url);
	}

	function getBannerContent(bannerEl) {
		if (bannerEl.bannerContent && bannerEl.bannerContent.nodeType === 1) {
			return bannerEl.bannerContent;
		}
		const nested = bannerEl.querySelector("li");
		if (nested) {
			bannerEl.bannerContent = nested;
			return nested;
		}
		return null;
	}

	function getBannerHost(bannerEl) {
		return bannerEl.querySelector("ul") || bannerEl;
	}

	function parkBannerContent(bannerEl, bannerContent) {
		const host = getBannerHost(bannerEl);
		if (bannerContent.parentNode !== host) {
			host.appendChild(bannerContent);
		}
	}

	function isPlaceableGridItem(el) {
		return Boolean(el && el.nodeType === 1 && el.tagName === "LI" && !isCopyBlockItem(el));
	}

	function firstPlaceableItem(productGrid) {
		return Array.from(productGrid.children).find(isPlaceableGridItem) || null;
	}

	function lastPlaceableItem(productGrid) {
		const items = Array.from(productGrid.children).filter(isPlaceableGridItem);
		return items[items.length - 1] || null;
	}

	/**
	 * Resolve a 1-based visual cell (products + already-placed info cards).
	 * Copy blocks are skipped. Returns start / before / after, or null to wait.
	 */
	function findVisualSlotPlacement(productGrid, slot) {
		if (!slot || slot <= 0) return null;

		if (slot <= 1) {
			const first = firstPlaceableItem(productGrid);
			return first ? { type: "before", anchor: first } : { type: "start" };
		}

		let count = 0;
		for (const item of productGrid.children) {
			if (!isPlaceableGridItem(item)) continue;
			count += 1;
			if (count === slot) {
				return { type: "before", anchor: item };
			}
		}

		if (moreProductsWillLoad()) return null;

		const last = lastPlaceableItem(productGrid);
		return last ? { type: "after", anchor: last } : { type: "start" };
	}

	function snapGridSlotToMobileRow(position, productGrid) {
		if (!isMobileBannerLayout() || !productGrid) return position;
		const cols = getMobileCols(productGrid);
		if (!cols || cols <= 1) return position;
		return Math.floor((position - 1) / cols) * cols + 1;
	}

	/**
	 * Placement intent:
	 * - { type: "after", anchor } after a product (after-product or after-row)
	 * - { type: "grid", slot } 1-based visual cell (explicit Position in the grid)
	 * - null hide
	 *
	 * Hide is required when the after-product is not in this collection AND
	 * Position in the grid is 0 / unset. Existing cards use that miss as a
	 * cheap hide on sibling collections that share the template (PR #107).
	 * 0 is also the Theme Editor default Shopify writes onto old blocks.
	 */
	function bannerVisibleAtBreakpoint(bannerEl) {
		const desktop = window.matchMedia("(min-width: 64em)").matches;
		const tablet = window.matchMedia("(min-width: 48em) and (max-width: 63.99em)").matches;
		const key = desktop ? "showDesktop" : tablet ? "showTablet" : "showMobile";
		const value = bannerEl.dataset[key];
		if (value == null || value === "") return true;
		return value === "true";
	}

	function computePlacement(products, bannerEl, productGrid) {
		const afterProductHandle = (bannerEl.dataset.afterProductHandle || "").trim();
		const afterProductId = (bannerEl.dataset.afterProductId || "").trim();
		const desktopGridPosition = parseInt(bannerEl.dataset.desktopGridPosition, 10) || 0;

		const mobileInsertMode = (bannerEl.dataset.mobileInsertMode || "same_as_desktop").trim();
		const mobileAfterRow = parseInt(bannerEl.dataset.mobileAfterRow, 10) || 0;

		function snapAnchorToMobileRowEnd(anchorProduct) {
			if (!isMobileBannerLayout()) return anchorProduct;
			if (!productGrid) return anchorProduct;

			const cols = getMobileCols(productGrid);
			if (!cols || cols <= 1) return anchorProduct;

			const idx = products.indexOf(anchorProduct);
			if (idx < 0) return anchorProduct;

			const oneBased = idx + 1;
			const row = Math.ceil(oneBased / cols);
			const rowEndOneBased = Math.min(row * cols, products.length);
			return products[rowEndOneBased - 1] || anchorProduct;
		}

		if (isMobileBannerLayout() && mobileInsertMode === "after_row") {
			const productsPerRow = getMobileCols(productGrid);
			const afterRow = mobileAfterRow;
			if (!afterRow || afterRow <= 0) return null;
			const lastProductOfTargetRow = Math.min(afterRow * productsPerRow, products.length);
			if (products.length < lastProductOfTargetRow) return null;
			const anchor = products[lastProductOfTargetRow - 1] || null;
			return anchor ? { type: "after", anchor } : null;
		}

		const anchorProduct = findAnchorProduct(products, afterProductHandle, afterProductId);
		if (anchorProduct) {
			return { type: "after", anchor: snapAnchorToMobileRowEnd(anchorProduct) };
		}

		if (desktopGridPosition > 0) {
			return { type: "grid", slot: snapGridSlotToMobileRow(desktopGridPosition, productGrid) };
		}

		return null;
	}

	function placementGroupKey(placement, products) {
		const idx = products.indexOf(placement.anchor);
		const item = placement.anchor && placement.anchor.dataset.itemNumber;
		return item ? `after-item-${item}` : `after-node-${idx}`;
	}

	function resolvedInsertionPoint(productGrid, resolved) {
		if (!resolved || resolved.type === "start") {
			return firstPlaceableItem(productGrid);
		}
		if (resolved.type === "before") return resolved.anchor || null;

		let insertionPoint = resolved.anchor ? resolved.anchor.nextSibling : null;
		while (
			insertionPoint &&
			insertionPoint.nodeType === Node.ELEMENT_NODE &&
			insertionPoint.tagName === "LI" &&
			isInfoCardItem(insertionPoint)
		) {
			insertionPoint = insertionPoint.nextSibling;
		}
		return insertionPoint;
	}

	function insertBannerItems(productGrid, items, resolved) {
		let insertionPoint = resolvedInsertionPoint(productGrid, resolved);

		for (const bannerContent of items) {
			bannerContent.style.display = "";
			if (
				bannerContent.parentNode === productGrid &&
				(bannerContent === insertionPoint || bannerContent.nextSibling === insertionPoint)
			) {
				insertionPoint = bannerContent.nextSibling;
				continue;
			}
			productGrid.insertBefore(bannerContent, insertionPoint);
			insertionPoint = bannerContent.nextSibling;
		}
	}

	function scheduleBannerPlacement(root = document) {
		if (bannersScheduled) return;
		bannersScheduled = true;
		window.requestAnimationFrame(() => {
			bannersScheduled = false;
			placeAllBanners(root);
		});
	}

	function placeAllBanners(root = document) {
		const scope = root || document;
		const productGrid = getProductGrid(scope) || getProductGrid(document);
		if (!productGrid) return;

		isRepositioning = true;
		try {
			const banners = Array.from(scope.querySelectorAll("collection-grid-banner"));
			const products = getProductCards(productGrid);

			if (banners.length && products.length) {
				const anchoredGroups = new Map();
				const gridItems = [];

				for (const bannerEl of banners) {
					const bannerContent = getBannerContent(bannerEl);
					if (!bannerContent) continue;

					if (!bannerVisibleAtBreakpoint(bannerEl)) {
						parkBannerContent(bannerEl, bannerContent);
						bannerContent.style.display = "none";
						continue;
					}

					const placement = computePlacement(products, bannerEl, productGrid);
					parkBannerContent(bannerEl, bannerContent);

					if (!placement) {
						bannerContent.style.display = "none";
						continue;
					}

					if (placement.type === "after") {
						const key = placementGroupKey(placement, products);
						if (!anchoredGroups.has(key)) anchoredGroups.set(key, { placement, items: [] });
						anchoredGroups.get(key).items.push(bannerContent);
						continue;
					}

					if (placement.type === "grid") {
						gridItems.push({ slot: placement.slot, bannerContent });
					}
				}

				const orderedAnchored = Array.from(anchoredGroups.values()).sort((a, b) => {
					return products.indexOf(a.placement.anchor) - products.indexOf(b.placement.anchor);
				});

				for (const group of orderedAnchored) {
					insertBannerItems(productGrid, group.items, group.placement);
				}

				const gridGroups = new Map();
				for (const item of gridItems) {
					if (!gridGroups.has(item.slot)) gridGroups.set(item.slot, []);
					gridGroups.get(item.slot).push(item.bannerContent);
				}

				for (const slot of Array.from(gridGroups.keys()).sort((a, b) => a - b)) {
					const resolved = findVisualSlotPlacement(productGrid, slot);
					const items = gridGroups.get(slot);
					if (!resolved) {
						for (const bannerContent of items) bannerContent.style.display = "none";
						continue;
					}
					insertBannerItems(productGrid, items, resolved);
				}
			}

			placeCopyBlocks(productGrid);
		} finally {
			// MutationObserver callbacks run after this synchronous placement finishes.
			// Drain our own moves before resetting the guard so they cannot loop and
			// detach a focused or pressed link on every animation frame.
			if (observer) observer.takeRecords();
			isRepositioning = false;
		}
	}

	function repositionCollectionBlocks(root = document) {
		const scope = root || document;
		scheduleBannerPlacement(scope);
	}

	if (!customElements.get("collection-grid-banner")) {
		class CollectionGridBanner extends HTMLElement {
			constructor() {
				super();
				this.bannerContent = null;
			}

			connectedCallback() {
				// Children exist here (not in the constructor during HTML parse).
				this.bannerContent = this.bannerContent || this.querySelector("li");
				// Initial placement.
				this.placeBanner();

				// Reposition on resize.
				this.resizeHandler = this.handleResize.bind(this);
				window.addEventListener("resize", this.resizeHandler);
			}

			disconnectedCallback() {
				window.removeEventListener("resize", this.resizeHandler);
			}

			handleResize() {
				this.placeBanner();
			}

			placeBanner() {
				// We do grouped placement across all banners to avoid collisions + MutationObserver loops.
				scheduleBannerPlacement(document);
			}
		}

		customElements.define("collection-grid-banner", CollectionGridBanner);
	}

	// Infinite scroll loader (used by collection-endless.liquid).
	// This is intentionally small and resilient: if it fails, the Next page link still works.
	if (!customElements.get("infinite-scroll")) {
		class InfiniteScroll extends HTMLElement {
			constructor() {
				super();
				this.productsGrid = null;
				this.loading = null;
				this.nextLink = null;
				this.observer = null;
				this.isLoading = false;
			}

			connectedCallback() {
				this.productsGrid = document.querySelector("#product-grid");
				if (!this.productsGrid) return;

				this.loading = this.querySelector(".loading-overlay");
				this.nextLink = this.querySelector("a[href]");

				// Hide the link when JS is active; we’ll re-show it on errors.
				if (this.nextLink) this.nextLink.classList.add("dn");

				if (this.dataset.lastPage === "true") return;
				if (!this.dataset.url) return;

				this.observer = new IntersectionObserver(
					(entries) => {
						const entry = entries && entries[0];
						if (!entry || !entry.isIntersecting) return;
						this.loadNextPage();
					},
					{ rootMargin: "0px 0px 600px 0px" }
				);
				this.observer.observe(this);
			}

			disconnectedCallback() {
				if (this.observer) this.observer.disconnect();
			}

			async loadNextPage() {
				if (this.isLoading) return;
				if (this.dataset.lastPage === "true") return;
				const url = this.dataset.url;
				if (!url) return;

				this.isLoading = true;
				if (this.loading) this.loading.classList.remove("dn");

				try {
					const response = await fetch(url);
					const text = await response.text();

					const html = document.createElement("div");
					html.innerHTML = text;

					const products = html.querySelector("#product-grid");
					const nextInfinite = html.querySelector("infinite-scroll");

					if (products && products.innerHTML.trim().length) {
						const existingItems = Array.from(
							this.productsGrid.querySelectorAll("li[data-item-number]")
						).filter((item) => !item.querySelector(".productitem--info-block"));
						const lastItemNumber =
							existingItems.length > 0
								? parseInt(existingItems[existingItems.length - 1].getAttribute("data-item-number"), 10) || 0
								: 0;

						// Renumber items in the response before inserting, so each item number is unique.
						const newItems = products.querySelectorAll("li[data-item-number]");
						newItems.forEach((item, index) => {
							item.setAttribute("data-item-number", String(lastItemNumber + index + 1));
						});

						this.productsGrid.insertAdjacentHTML("beforeend", products.innerHTML);
					}

					if (nextInfinite && nextInfinite.dataset && nextInfinite.dataset.url) {
						this.dataset.url = nextInfinite.dataset.url;
					} else {
						this.dataset.lastPage = "true";
						if (this.observer) this.observer.disconnect();
						this.remove();
					}
				} catch (e) {
					// Fallback: allow manual paging.
					if (this.nextLink) this.nextLink.classList.remove("dn");
					console.error(e);
				} finally {
					if (this.loading) this.loading.classList.add("dn");
					this.isLoading = false;
				}
			}
		}

		customElements.define("infinite-scroll", InfiniteScroll);
	}

	// Initial positioning.
	document.addEventListener("DOMContentLoaded", () => {
		repositionCollectionBlocks(document);
	});

	// Theme editor: re-run positioning when Shopify re-renders the section.
	document.addEventListener("shopify:section:load", (event) => repositionCollectionBlocks(event.target));
	document.addEventListener("shopify:section:select", (event) => repositionCollectionBlocks(event.target));

	// Dynamic grid updates (infinite scroll inserts, etc.)
	let observer;
	function ensureObserver() {
		const grid = getProductGrid(document);
		if (!grid) return;
		if (observer) return;

		observer = new MutationObserver(() => {
			// Ignore mutations we caused ourselves (prevents reorder thrash).
			if (isRepositioning) return;
			repositionCollectionBlocks(document);
		});
		observer.observe(grid, { childList: true });
	}

	// Try immediately and again after DOM is ready.
	ensureObserver();
	document.addEventListener("DOMContentLoaded", ensureObserver);
	document.addEventListener("collection:filters-updated", () => {
		if (observer) observer.disconnect();
		observer = null;
		ensureObserver();
		repositionCollectionBlocks(document);
	});
})();

